import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Play, Clock, Search, X, Youtube, Flame, ArrowRight, BookOpen, Quote, Mic } from 'lucide-react';
import { api } from '../lib/api';
import { fuzzyMatch } from '../lib/fuzzy';
import { getQuoteFor, getHighlightFor, youtubeTimestampUrl } from '../lib/guestQuotes';
import { useProgress, formatSeconds } from '../lib/progress';
import EpisodePlayerModal from '../components/EpisodePlayerModal';
import ShareButton from '../components/ShareButton';
import BookmarkButton from '../components/BookmarkButton';
import Newsletter from '../components/Newsletter';

const SPRING = { type: 'spring', stiffness: 700, damping: 22, mass: 0.6 };
const REVEAL = { type: 'spring', stiffness: 520, damping: 26 };
const NO_ANIM = { duration: 0 };

const PAGE_SIZE = 8;

// Match Tailwind's `sm` breakpoint (640px) so we can lighten JS work on phones.
const useIsMobile = () => {
  const [mobile, setMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return mobile;
};

const stripMd = (t) => (t || '').replace(/\*\*/g, '');
const isNew = (isoDate) => {
  if (!isoDate) return false;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return false;
  return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24) <= 7;
};

// ============================================================
// Featured hero (full-width anchor at top)
// ============================================================
const FeaturedEpisode = ({ ep, onPlay, lite }) => {
  if (!ep) return null;
  const guestQuote = getQuoteFor(ep);
  const Article = lite ? 'article' : motion.article;
  const artProps = lite
    ? {}
    : {
        initial: { opacity: 0, y: 30, scale: 0.98 },
        animate: { opacity: 1, y: 0, scale: 1 },
        transition: REVEAL,
      };
  return (
    <Article
      {...artProps}
      className="relative overflow-hidden rounded-3xl glass-strong border border-[#EF5B5B]/25"
      data-testid="featured-episode"
      style={{
        boxShadow: '0 0 0 1px rgba(239,91,91,0.18), 0 30px 80px -30px rgba(239,91,91,0.45)',
      }}
    >
      <div className="grid md:grid-cols-5 gap-0">
        <button
          type="button"
          onClick={() => onPlay(ep)}
          className="relative md:col-span-2 aspect-square md:aspect-auto md:min-h-[420px] overflow-hidden group"
          aria-label={`Play ${ep.title}`}
        >
          <img
            src={ep.cover_art}
            alt={ep.title}
            className="w-full h-full object-cover group-hover:scale-105 smooth"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 smooth bg-black/30 backdrop-blur-sm">
            <div className="w-20 h-20 rounded-full bg-[#EF5B5B] flex items-center justify-center glow-red">
              <Play className="w-10 h-10 text-white ml-1" fill="currentColor" />
            </div>
          </div>
          <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-[#EF5B5B] text-white text-[10px] font-black tracking-[0.25em] uppercase shadow-lg">
            Featured
          </span>
        </button>

        <div className="md:col-span-3 p-8 sm:p-10 lg:p-14 flex flex-col justify-center">
          {(ep.categories || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {ep.categories.slice(0, 3).map((c) => (
                <span key={c} className="px-2.5 py-1 rounded-md bg-[#EF5B5B]/12 text-[#EF5B5B] text-[11px] font-semibold uppercase tracking-wider">
                  {c}
                </span>
              ))}
            </div>
          )}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
            {ep.title}
          </h2>
          <p className="text-base sm:text-lg text-gray-300/90 leading-relaxed mb-6 line-clamp-4">
            {stripMd(ep.description)}
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500 mb-8">
            {ep.duration && (
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" />{ep.duration}</span>
            )}
            {ep.published_at && (
              <span>{new Date(ep.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <motion.button
              type="button"
              onClick={() => onPlay(ep)}
              data-testid="featured-play-button"
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={SPRING}
              className="inline-flex items-center gap-2 px-7 py-3.5 btn-red rounded-full font-bold text-base"
            >
              <Play className="w-5 h-5" fill="currentColor" /> Play Episode
            </motion.button>
            {ep.youtube_id && (
              <a
                href={`https://www.youtube.com/watch?v=${ep.youtube_id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="featured-youtube-link"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full glass border border-white/10 hover:border-[#EF5B5B]/60 hover:text-[#EF5B5B] text-sm font-semibold smooth"
              >
                <Youtube className="w-4 h-4" /> Watch on YouTube
              </a>
            )}
            <ShareButton
              url={ep.youtube_id ? `https://www.youtube.com/watch?v=${ep.youtube_id}` : (ep.apple_url || window.location.href)}
              title={ep.title}
              testId={`featured-share-${ep.id}`}
            />
            <BookmarkButton ep={ep} size="lg" testId={`featured-bookmark-${ep.id}`} />
          </div>

          {guestQuote && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={REVEAL}
              className="mt-6 p-4 rounded-2xl border border-[#EF5B5B]/25 flex gap-3"
              data-testid="episode-key-insight"
              style={{
                background: 'linear-gradient(90deg, rgba(239,91,91,0.12), rgba(239,91,91,0.03))',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Quote className="w-5 h-5 text-[#EF5B5B] shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#EF5B5B] mb-1.5">
                  Key Insight
                </p>
                <p className="text-sm sm:text-base text-gray-100 italic leading-snug">
                  {guestQuote.quote}
                </p>
                {ep.youtube_id && (
                  <a
                    href={youtubeTimestampUrl(ep.youtube_id, guestQuote.timestamp)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`key-insight-timestamp-${ep.id}`}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#EF5B5B] hover:underline"
                  >
                    Jump to {guestQuote.timestamp} →
                  </a>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Article>
  );
};

// A "Listen now" / "Resume X:XX" button that adapts based on saved progress.
const PlayOrResume = ({ ep, onPlay, testIdPlay }) => {
  const p = useProgress(ep.id);
  const hasProgress = p && p.seconds >= 30;
  return (
    <div className="inline-flex items-center gap-3">
      <button
        type="button"
        onClick={() => onPlay(ep)}
        data-testid={testIdPlay}
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#EF5B5B] hover:gap-3 smooth"
      >
        {hasProgress ? 'Restart' : 'Listen now'} <ArrowRight className="w-4 h-4" />
      </button>
      {hasProgress && (
        <button
          type="button"
          onClick={() => onPlay(ep, p.seconds)}
          data-testid={`resume-${ep.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EF5B5B]/12 border border-[#EF5B5B]/30 text-[11px] font-black uppercase tracking-widest text-[#EF5B5B] hover:bg-[#EF5B5B]/20 smooth"
        >
          Resume {formatSeconds(p.seconds)}
        </button>
      )}
    </div>
  );
};

// ============================================================
// Latest feed row (narrative summary — magazine style)
// ============================================================
const LatestRow = ({ ep, onPlay, index, reflection, lite }) => {
  const guestQuote = getHighlightFor(ep);
  const MotionArticle = lite ? 'article' : motion.article;
  const motionProps = lite
    ? {}
    : {
        variants: {
          hidden: { opacity: 0, y: 24, scale: 0.97 },
          show: { opacity: 1, y: 0, scale: 1, transition: REVEAL },
        },
        whileHover: { y: -3 },
        transition: SPRING,
      };
  return (
  <MotionArticle
    {...motionProps}
    className="episode-card group relative flex flex-col sm:flex-row gap-5 sm:gap-6 p-4 sm:p-5 rounded-2xl glass-strong will-change-transform"
    data-testid="latest-episode-row"
  >
    <button
      type="button"
      onClick={() => onPlay(ep)}
      className="relative shrink-0 w-full sm:w-52 aspect-square rounded-xl overflow-hidden group/thumb"
      aria-label={`Play ${ep.title}`}
    >
      <img src={ep.cover_art} alt={ep.title} className="w-full h-full object-cover group-hover/thumb:scale-105 smooth" loading="lazy" />
      <div className="absolute inset-0 bg-black/25 opacity-0 group-hover/thumb:opacity-100 smooth flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-[#EF5B5B] flex items-center justify-center glow-red">
          <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />
        </div>
      </div>
      {isNew(ep.published_at) && (
        <motion.span
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={SPRING}
          className="absolute top-2.5 left-2.5 px-2.5 py-1 rounded-full bg-[#EF5B5B] text-white text-[10px] font-black uppercase tracking-widest shadow-[0_0_18px_rgba(239,91,91,0.75)]"
          data-testid="episode-new-badge"
        >
          New
        </motion.span>
      )}
      {ep.duration && (
        <span className="absolute bottom-2.5 right-2.5 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-sm text-white text-[11px] font-semibold flex items-center gap-1 border border-white/10">
          <Clock className="w-3 h-3" /> {ep.duration}
        </span>
      )}
    </button>

    <div className="flex-1 min-w-0 flex flex-col">
      <div className="flex items-center gap-3 text-[11px] text-gray-500 uppercase tracking-widest mb-2">
        <span>Episode {String(ep.episode_number ?? (index + 1)).padStart(2, '0')}</span>
        {ep.published_at && (
          <>
            <span className="w-1 h-1 rounded-full bg-gray-600" />
            <span>{new Date(ep.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </>
        )}
        {ep.duration && (
          <>
            <span className="w-1 h-1 rounded-full bg-gray-600" />
            <span>{ep.duration}</span>
          </>
        )}
      </div>
      <h3 className="text-xl sm:text-2xl font-bold leading-snug mb-3 group-hover:text-[#EF5B5B] smooth line-clamp-2">
        {ep.title}
      </h3>

      {/* Reflection excerpt — the "reflection text directly below the title" */}
      {reflection && (reflection.excerpt || reflection.title) && (
        <div className="mb-4 pl-4 border-l-2 border-[#EF5B5B]/60" data-testid="episode-reflection-block">
          <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#EF5B5B] mb-1">
            Host&apos;s Reflection
          </div>
          {reflection.title && reflection.title !== ep.title && (
            <p className="text-sm font-semibold text-gray-200 leading-snug mb-1.5 line-clamp-2">
              {reflection.title}
            </p>
          )}
          <p className="text-sm text-gray-400 leading-relaxed line-clamp-3">
            {reflection.excerpt}
          </p>
          <Link
            to={`/blog/${reflection.slug}`}
            data-testid={`read-reflection-${reflection.slug}`}
            className="inline-flex items-center gap-1.5 mt-2 text-xs font-bold text-[#EF5B5B] hover:gap-2 smooth"
          >
            <BookOpen className="w-3.5 h-3.5" /> Read the full reflection
          </Link>
        </div>
      )}

      {(!reflection || !reflection.excerpt) && (
        <p className="text-sm sm:text-[15px] text-gray-400 leading-relaxed line-clamp-3 mb-4 flex-1">
          {stripMd(ep.description)}
        </p>
      )}

      {/* Key Insight — verbatim guest quote with clickable timestamp deep-link */}
      {guestQuote && (
        <div
          className="mb-4 p-3.5 rounded-xl border border-[#EF5B5B]/25 flex gap-3"
          data-testid="episode-key-insight"
          style={{
            background:
              'linear-gradient(90deg, rgba(239,91,91,0.10), rgba(239,91,91,0.02))',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Quote className="w-4 h-4 text-[#EF5B5B] shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#EF5B5B] mb-1">
              {guestQuote.verbatim && guestQuote.speaker ? guestQuote.speaker : 'Highlight'}
            </p>
            <p className="text-[13.5px] sm:text-sm text-gray-100 italic leading-snug">
              {guestQuote.verbatim ? `“${guestQuote.quote}”` : guestQuote.quote}
            </p>
            {ep.youtube_id && guestQuote.timestamp && (
              <a
                href={youtubeTimestampUrl(ep.youtube_id, guestQuote.timestamp)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`key-insight-timestamp-${ep.id}`}
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-[#EF5B5B] hover:underline"
              >
                Jump to {guestQuote.timestamp} →
              </a>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-auto">
        <PlayOrResume ep={ep} onPlay={onPlay} testIdPlay="latest-play-button" />
        <ShareButton
          url={ep.youtube_id ? `https://www.youtube.com/watch?v=${ep.youtube_id}` : (ep.apple_url || window.location.href)}
          title={ep.title}
          testId={`latest-share-${ep.id}`}
        />
        <BookmarkButton ep={ep} size="md" testId={`latest-bookmark-${ep.id}`} />
      </div>
    </div>
  </MotionArticle>
  );
};

// ============================================================
// Trending sidebar (text-heavy, tiny thumbs)
// ============================================================
const TrendingSidebar = ({ items, onPlay }) => (
  <aside
    className="lg:sticky lg:top-28 lg:self-start rounded-3xl p-6 border border-[#EF5B5B]/25"
    data-testid="trending-sidebar"
    style={{
      background:
        'radial-gradient(120% 90% at 100% 0%, rgba(239,91,91,0.14), transparent 55%),' +
        'linear-gradient(180deg, rgba(22,14,16,0.85), rgba(10,10,10,0.9))',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      boxShadow: '0 0 0 1px rgba(239,91,91,0.15), 0 30px 60px -30px rgba(239,91,91,0.35)',
    }}
  >
    <div className="flex items-center gap-2 mb-5 pb-4 border-b border-white/10">
      <Flame className="w-5 h-5 text-[#EF5B5B]" />
      <h3 className="text-sm font-black uppercase tracking-[0.3em] text-gray-200">Trending Now</h3>
    </div>
    <ol className="space-y-4">
      {items.map((ep, i) => {
        const q = getQuoteFor(ep);
        return (
        <motion.li
          key={ep.id}
          initial={{ opacity: 0, x: 12 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ ...REVEAL, delay: i * 0.05 }}
        >
          <div className="relative flex items-start gap-3 group">
            <button
              type="button"
              onClick={() => onPlay(ep)}
              data-testid={`trending-item-${ep.id}`}
              className="flex-1 text-left flex items-start gap-3 min-w-0"
            >
              <span className="text-2xl font-black text-[#EF5B5B]/50 group-hover:text-[#EF5B5B] smooth leading-none w-6 mt-1">
                {String(i + 1).padStart(2, '0')}
              </span>
              <img
                src={ep.cover_art}
                alt=""
                className="w-14 h-14 rounded-md object-cover shrink-0"
                loading="lazy"
              />
              <div className="flex-1 min-w-0 pr-8">
                <p className="text-sm font-bold leading-snug line-clamp-2 group-hover:text-[#EF5B5B] smooth">
                  {ep.title}
                </p>
                <p className="text-[11px] text-gray-500 mt-1">
                  {ep.duration || (ep.published_at ? new Date(ep.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '')}
                </p>
              </div>
            </button>
            <div className="absolute top-0 right-0">
              <BookmarkButton ep={ep} size="sm" testId={`trending-bookmark-${ep.id}`} />
            </div>
          </div>

          {q && ep.youtube_id && (
            <div
              className="mt-2 ml-9 pl-3 border-l-2 border-[#EF5B5B]/40"
              data-testid="episode-key-insight"
            >
              <p className="text-[12px] italic text-gray-300 leading-snug line-clamp-2">
                “{q.quote}”
              </p>
              <a
                href={youtubeTimestampUrl(ep.youtube_id, q.timestamp)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                data-testid={`key-insight-timestamp-${ep.id}`}
                className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-[#EF5B5B] hover:underline"
              >
                Jump to {q.timestamp} →
              </a>
            </div>
          )}
        </motion.li>
        );
      })}
    </ol>

    {/* Become a Speaker CTA */}
    <div className="mt-6 pt-6 border-t border-white/10">
      <Link
        to="/apply"
        data-testid="trending-become-speaker"
        className="group inline-flex items-center justify-center w-full gap-2 px-5 py-3.5 btn-red rounded-full text-sm font-bold"
      >
        <Mic className="w-4 h-4" />
        Become a Speaker
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 smooth" />
      </Link>
    </div>
  </aside>
);

// ============================================================
// Main page
// ============================================================
const EpisodesPage = () => {
  const [episodes, setEpisodes] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [activeEpisode, setActiveEpisode] = useState(null);
  const [resumeFrom, setResumeFrom] = useState(0);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const liteMotion = isMobile || prefersReducedMotion;

  // Reset pagination whenever the filter changes
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [searchQuery, selectedCategory]);

  useEffect(() => {
    Promise.all([
      api.get('/episodes?type=video'),
      api.get('/posts'),
    ])
      .then(([e, p]) => { setEpisodes(e.data); setPosts(p.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Assign an episode_number to every episode based on publish date ascending,
  // then sort the working set descending so newest (highest #) is first.
  const numberedEpisodes = useMemo(() => {
    const ascending = [...episodes].sort((a, b) => (a.published_at || '').localeCompare(b.published_at || ''));
    const numberMap = new Map();
    ascending.forEach((e, i) => numberMap.set(e.id, i + 1));
    return episodes.map((e) => ({ ...e, episode_number: numberMap.get(e.id) }));
  }, [episodes]);

  // Fast slug → post lookup so every episode row can render its reflection text.
  const postBySlug = useMemo(() => {
    const m = new Map();
    posts.forEach((p) => m.set(p.slug, p));
    return m;
  }, [posts]);

  const getReflection = (ep) => (ep.reflection_slug ? postBySlug.get(ep.reflection_slug) : undefined);

  const categories = useMemo(() => {
    const set = new Set();
    numberedEpisodes.forEach((e) => (e.categories || []).forEach((c) => set.add(c)));
    return ['All', ...Array.from(set).sort()];
  }, [numberedEpisodes]);

  const isFiltering = searchQuery.trim() !== '' || selectedCategory !== 'All';

  const filtered = useMemo(() => {
    const q = searchQuery.trim();
    if (!isFiltering) return numberedEpisodes;
    return numberedEpisodes
      .map((ep) => {
        const matchesCat = selectedCategory === 'All' || (ep.categories || []).includes(selectedCategory);
        if (!matchesCat) return null;
        if (!q) return { ep, score: 1 };
        const score = fuzzyMatch(ep, q, [
          { get: (e) => e.title || '', weight: 1.0 },
          { get: (e) => (e.categories || []).join(' '), weight: 0.85 },
          { get: (e) => (e.description || '').replace(/\*\*/g, ''), weight: 0.7 },
          { get: (e) => e.reflection || '', weight: 0.55 },
        ]);
        return score >= 0.35 ? { ep, score } : null;
      })
      .filter(Boolean)
      .sort((a, b) => (b.ep.episode_number || 0) - (a.ep.episode_number || 0))
      .map((x) => x.ep);
  }, [numberedEpisodes, searchQuery, selectedCategory, isFiltering]);

  // Magazine layout — sort by episode_number DESC so newest (highest #) leads.
  const [featured, trending, latest] = useMemo(() => {
    if (!numberedEpisodes.length) return [null, [], []];
    const sorted = [...numberedEpisodes].sort((a, b) => (b.episode_number || 0) - (a.episode_number || 0));
    const feat = sorted[0];
    const trendingSlugs = new Set([
      'first-female-afp-spokesperson-ai-leadership',
      'the-global-challenge-of-ai-regulation',
      'taxing-the-future-how-ai-is-transforming-global-taxation',
    ]);
    let trend = sorted.filter((e) => trendingSlugs.has(e.reflection_slug) && e.id !== feat.id).slice(0, 3);
    if (trend.length < 3) {
      const picked = new Set(trend.map((e) => e.id));
      // Fill from the oldest end so the newest episodes stay in the Latest feed.
      for (const e of [...sorted].reverse()) {
        if (trend.length >= 3) break;
        if (e.id === feat.id || picked.has(e.id)) continue;
        trend.push(e);
        picked.add(e.id);
      }
    }
    const trendIds = new Set(trend.map((e) => e.id));
    const rest = sorted.filter((e) => e.id !== feat.id && !trendIds.has(e.id));
    return [feat, trend, rest];
  }, [numberedEpisodes]);

  const onPlay = (ep, seekTo = 0) => { setResumeFrom(seekTo); setActiveEpisode(ep); };

  return (
    <div className="pt-28 pb-16 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Page header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={REVEAL}
          className="mb-8 sm:mb-10"
        >
          <span className="inline-block px-4 py-1.5 rounded-full glass text-[11px] tracking-[0.3em] uppercase text-gray-300 mb-4">
            The Podcast
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
            All <span className="gradient-text">Episodes</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 mt-3 max-w-2xl">
            Deep dives into AI, business, and leadership — with the executives, founders, and researchers reshaping the future.
          </p>
        </motion.header>

        {/* Search + Filters */}
        <div className="mb-10 space-y-4">
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search episodes by title, guest, or topic…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="episode-search-input"
              className="w-full pl-12 pr-12 py-4 glass-strong rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#EF5B5B]/50 smooth"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10"
                aria-label="Clear search"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          {categories.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <motion.button
                  key={c}
                  onClick={() => setSelectedCategory(c)}
                  data-testid={`filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  transition={SPRING}
                  className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold smooth ${
                    selectedCategory === c
                      ? 'bg-[#EF5B5B] text-white shadow-[0_0_18px_rgba(239,91,91,0.55)]'
                      : 'glass hover:bg-white/10 text-gray-300'
                  }`}
                >
                  {c}
                </motion.button>
              ))}
            </div>
          )}
          {isFiltering && (
            <p className="text-sm text-gray-500" data-testid="episode-count">
              Found {filtered.length} episode{filtered.length !== 1 ? 's' : ''}
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
            </p>
          )}
        </div>

        {loading ? (
          <p className="text-gray-500">Loading episodes…</p>
        ) : (
          <AnimatePresence mode="wait">
            {isFiltering ? (
              /* Filter results: clean grid */
              <motion.div
                key="filtered"
                initial={liteMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={liteMotion ? NO_ANIM : { duration: 0.15 }}
              >
                {filtered.length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-gray-400 mb-4">No episodes match your search.</p>
                    <button
                      onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
                      className="px-6 py-3 btn-red rounded-full font-semibold"
                    >
                      Clear Filters
                    </button>
                  </div>
                ) : (
                  <>
                    {liteMotion ? (
                      <div className="grid gap-5">
                        {filtered.slice(0, isMobile ? visibleCount : filtered.length).map((ep, i) => (
                          <LatestRow key={ep.id} ep={ep} onPlay={onPlay} index={i} reflection={getReflection(ep)} lite />
                        ))}
                      </div>
                    ) : (
                      <motion.div
                        initial="hidden"
                        animate="show"
                        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.03 } } }}
                        className="grid gap-5"
                      >
                        {filtered.map((ep, i) => (
                          <LatestRow key={ep.id} ep={ep} onPlay={onPlay} index={i} reflection={getReflection(ep)} />
                        ))}
                      </motion.div>
                    )}
                    {isMobile && visibleCount < filtered.length && (
                      <div className="flex justify-center mt-8">
                        <button
                          type="button"
                          onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                          data-testid="load-more-button"
                          className="px-8 py-3.5 rounded-full glass border border-[#EF5B5B]/40 hover:border-[#EF5B5B] text-sm font-bold smooth"
                        >
                          Load {Math.min(PAGE_SIZE, filtered.length - visibleCount)} more
                        </button>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="magazine"
                initial={liteMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={liteMotion ? NO_ANIM : { duration: 0.15 }}
              >
                {/* Featured anchor */}
                <FeaturedEpisode ep={featured} onPlay={onPlay} lite={liteMotion} />

                {/* Magazine layout: latest feed + trending sidebar */}
                <div className="mt-12 sm:mt-16 grid lg:grid-cols-[1fr_320px] gap-10 items-start">
                  <section>
                    <div className="flex items-baseline justify-between mb-6">
                      <h2 className="text-2xl sm:text-3xl font-bold">
                        Latest <span className="gradient-text">Episodes</span>
                      </h2>
                      <span className="text-xs text-gray-500 tracking-widest uppercase">
                        {latest.length} more
                      </span>
                    </div>
                    {liteMotion ? (
                      <div className="grid gap-5">
                        {latest.slice(0, isMobile ? visibleCount : latest.length).map((ep, i) => (
                          <LatestRow key={ep.id} ep={ep} onPlay={onPlay} index={i} reflection={getReflection(ep)} lite />
                        ))}
                      </div>
                    ) : (
                      <motion.div
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: '-60px' }}
                        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.035 } } }}
                        className="grid gap-5"
                      >
                        {latest.map((ep, i) => (
                          <LatestRow key={ep.id} ep={ep} onPlay={onPlay} index={i} reflection={getReflection(ep)} />
                        ))}
                      </motion.div>
                    )}
                    {isMobile && visibleCount < latest.length && (
                      <div className="flex justify-center mt-8">
                        <button
                          type="button"
                          onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                          data-testid="load-more-button"
                          className="px-8 py-3.5 rounded-full glass border border-[#EF5B5B]/40 hover:border-[#EF5B5B] text-sm font-bold smooth"
                        >
                          Load {Math.min(PAGE_SIZE, latest.length - visibleCount)} more
                        </button>
                      </div>
                    )}
                  </section>

                  <TrendingSidebar items={trending} onPlay={onPlay} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>

      <div className="mt-20">
        <Newsletter />
      </div>

      <EpisodePlayerModal
        episode={activeEpisode}
        open={!!activeEpisode}
        onClose={() => setActiveEpisode(null)}
        resumeFrom={resumeFrom}
      />
    </div>
  );
};

export default EpisodesPage;
